import { CircularProgress, Grid, Pagination, Paper } from '@mui/material';

import clsx from 'clsx';
import { FieldArray, useFormikContext } from 'formik';
import { truncate } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useJobmarketPeriodsForOffersQuery } from 'api/hooks/jobmarket/invoices';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { RichList } from 'app/components/RichList';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { InvoiceFormValues } from '../validation';

export default function Periods() {
  const { values } = useFormikContext<InvoiceFormValues>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);

  const {
    data: periodsData,
    isError,
    isLoading
  } = useJobmarketPeriodsForOffersQuery({
    params: { offers: values.selectedOffers.map(o => o.id), offset }
  });

  const handlePeriodSelect = (period, helpers) => {
    const idx = values.selectedPeriods.findIndex(p => p.id === period.id);
    if (idx >= 0) {
      helpers.remove(idx);
    } else {
      helpers.push(period);
    }
  };

  const handlePagination = (event, page: number) => {
    const offset = (page - 1) * 10;
    setOffset(offset);
  };

  if (isError) {
    dispatch(AlertActions.error(`${t(translations.pages.invoices.alerts.errorLoading())}`));
    navigate('/', { replace: true });
    return;
  }

  if (isLoading) {
    return <CircularProgress />;
  }
  const periods = periodsData!;

  return (
    <Grid container>
      <ErrorBoundary boundaryTag="invoicePeriodsBoundary">
        <Grid container className="invoice-detail-modal__body__offer-count">
          {t(translations.pages.invoices.selectedPeriodsCount(), {
            selected: values.selectedPeriods.length,
            total: periods.pagination.count
          })}
        </Grid>
        <RichList
          staticHeading={
            <Grid
              container
              className="invoice-detail-modal__body__list-heading"
              component={Paper}
              elevation={3}
              square
            >
              <Grid item md={2} className="invoice-detail-modal__body__list-heading__item">
                {t(translations.pages.invoices.periodId())}
              </Grid>
              <Grid item md={5} className="invoice-detail-modal__body__list-heading__item">
                {t(translations.pages.invoices.offerIdTitle())}
              </Grid>
              <Grid item md={2} className="invoice-detail-modal__body__list-heading__item">
                {t(translations.pages.articles.filter.startDate())}
              </Grid>
              <Grid item md={2} className="invoice-detail-modal__body__list-heading__item">
                {t(translations.pages.articles.filter.endDate())}
              </Grid>
              <Grid item md={1} className="invoice-detail-modal__body__list-heading__item">
                {t(translations.pages.manageJobmarketSettings.price())}
              </Grid>
            </Grid>
          }
        >
          <>
            <Grid container className="invoice-detail-modal__body__offers">
              <FieldArray name="selectedPeriods">
                {({ form, ...helpers }) => {
                  return periods.data.map(p => (
                    <ErrorBoundary key={p.id} boundaryTag="paidOffersItemBoundary">
                      <Grid
                        key={p.id}
                        container
                        className={clsx('invoice-detail-modal__body__offers__offer', {
                          'invoice-detail-modal__body__offers__offer--selected':
                            values.selectedPeriods.findIndex(sp => sp?.id === p.id) !== -1
                        })}
                        onClick={() =>
                          handlePeriodSelect(
                            {
                              id: p.id,
                              price: p.jobPrice?.price,
                              offerId: p.jobOffering?.id
                            },
                            helpers
                          )
                        }
                      >
                        <Grid item md={2}>
                          {p.id}
                        </Grid>
                        <Grid
                          item
                          md={5}
                          className="invoice-detail-modal__body__offers__offer__title"
                        >
                          {p.jobOffering?.id}{' '}
                          <span className="invoice-detail-modal__body__offers__offer__title__sub">
                            [{truncate(p.jobOffering?.title, { length: 50 })}]
                          </span>
                        </Grid>
                        <Grid
                          item
                          md={2}
                          className="invoice-detail-modal__body__offers__offer__date"
                        >
                          {new Date(p.startDate).toLocaleDateString()}
                        </Grid>
                        <Grid
                          item
                          md={2}
                          className="invoice-detail-modal__body__offers__offer__date"
                        >
                          {new Date(p.endDate).toLocaleDateString()}
                        </Grid>
                        <Grid item md={1}>
                          {p.jobPrice?.price}€
                        </Grid>
                      </Grid>
                    </ErrorBoundary>
                  ));
                }}
              </FieldArray>
            </Grid>
          </>
        </RichList>
        {Math.floor(periods.pagination.count! / 10) > 0 && (
          <Grid container className="invoice-detail-modal__body__pagination">
            <Pagination
              count={Math.ceil(periods.pagination.count! / 10)}
              onChange={handlePagination}
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              size="medium"
              color="primary"
              siblingCount={2}
              boundaryCount={2}
            />
          </Grid>
        )}
      </ErrorBoundary>
    </Grid>
  );
}
